import { computed } from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import AiPromptsService, {
  type AiPrompts,
  type ResolvedAiPrompts,
} from "@/services/organization/ai-prompts";

export function useOrgAiPrompts() {
  return useQuery({
    queryKey: ["orgAiPrompts"],
    queryFn: async (): Promise<AiPrompts> => {
      const result = await AiPromptsService.getOrgAiPrompts();
      if (result.data.status && result.data.data) {
        return result.data.data;
      }
      throw new Error("Failed to load organization AI prompts");
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useUserAiPrompts() {
  return useQuery({
    queryKey: ["userAiPrompts"],
    queryFn: async (): Promise<AiPrompts> => {
      const result = await AiPromptsService.getUserAiPrompts();
      if (result.data.status && result.data.data) {
        return result.data.data;
      }
      throw new Error("Failed to load user AI prompts");
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useResolvedAiPrompts() {
  const query = useQuery({
    queryKey: ["resolvedAiPrompts"],
    queryFn: async (): Promise<ResolvedAiPrompts> => {
      const result = await AiPromptsService.getResolvedAiPrompts();
      if (result.data.status && result.data.data) {
        return result.data.data;
      }
      throw new Error("Failed to load resolved AI prompts");
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const composeAiPrompt = computed(() => query.data.value?.aiPromptCompose?.value ?? null);

  return { ...query, composeAiPrompt };
}

export function useUpdateOrgAiPrompts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<AiPrompts>) => {
      const result = await AiPromptsService.updateOrgAiPrompts(data);
      if (result.data.status) {
        return result.data.data;
      }
      throw new Error("Failed to update organization AI prompts");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orgAiPrompts"] });
      queryClient.invalidateQueries({ queryKey: ["resolvedAiPrompts"] });
    },
  });
}

export function useUpdateUserAiPrompts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<AiPrompts>) => {
      const result = await AiPromptsService.updateUserAiPrompts(data);
      if (result.data.status) {
        return result.data.data;
      }
      throw new Error("Failed to update user AI prompts");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userAiPrompts"] });
      queryClient.invalidateQueries({ queryKey: ["resolvedAiPrompts"] });
    },
  });
}
